import { HttpClient, HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';

/**
 * Downloads a file from a protected endpoint (e.g. /File/Download*) with the bearer token attached
 * (AbpHttpInterceptor adds the Authorization header), then hands the blob to the browser.
 * A plain `location.href = url` cannot send the Authorization header, so it gets HTTP 401 once the
 * endpoint requires authentication.
 *
 * @param fallbackFileName used when the server's Content-Disposition header is not readable (cross-origin).
 */
export function downloadWithAuth(http: HttpClient, url: string, fallbackFileName: string): void {
    http.get(url, { responseType: 'blob', observe: 'response' }).subscribe({
        next: (res: HttpResponse<Blob>) => {
            const serverName = fileNameFromContentDisposition(res.headers.get('Content-Disposition'));
            if (serverName) {
                saveAs(res.body, serverName);
                return;
            }
            // Content-Disposition is not readable cross-origin (CORS), so the server's real file name is
            // unknown. Job exports may be .csv or .xlsx while the server always labels them as Excel,
            // so pick the extension from the file content instead of trusting the fallback blindly.
            fixExtensionFromContent(res.body, fallbackFileName).then(name => saveAs(res.body, name));
        },
        error: () => {
            abp.message.error('Unable to download the file. It may no longer exist, or you may not have permission to access it.');
        }
    });
}

export function timestampedName(prefix: string, extension: string): string {
    const d = new Date();
    const p = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${prefix}_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}.${extension}`;
}

async function fixExtensionFromContent(blob: Blob, fileName: string): Promise<string> {
    try {
        if (!/\.xlsx$/i.test(fileName)) {
            return fileName;
        }
        const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
        const isZip = head[0] === 0x50 && head[1] === 0x4b; // "PK" - real .xlsx files are zip containers
        return isZip ? fileName : fileName.replace(/\.xlsx$/i, '.csv');
    } catch {
        return fileName;
    }
}

function fileNameFromContentDisposition(header: string | null): string | null {
    if (!header) {
        return null;
    }
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8) {
        try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ }
    }
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain ? plain[1] : null;
}
